var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * YearlyCalendar Component
 * Main shared component that orchestrates all calendar views
 */
import React, { useMemo, useCallback, useEffect, memo } from 'react';
import { useCalendarNavigation } from '../hooks/useCalendarNavigation';
import { useCalendarGrid } from '../hooks/useCalendarGrid';
import { useEventProcessing } from '../hooks/useEventProcessing';
import { usePopupState } from '../hooks/usePopupState';
import { useCalendarCallbacks } from '../hooks/useCalendarCallbacks';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';
import { CalendarNavigation } from './CalendarNavigation';
import { DayPicker } from './DayPicker';
import { MonthPicker } from './MonthPicker';
import { YearPicker } from './YearPicker';
import { YearView } from './YearView';
import { CalendarPopup } from './CalendarPopup';
import { DEFAULT_LEGEND_ITEMS } from './EventLegend/EventLegend';
import { DEFAULT_CALENDAR_CONFIG } from '../../../../Configuration/YearlyCalendarConfiguration/CalendarConfig';
import { DEFAULT_POPUP_SETTINGS } from '../../../../Configuration/YearlyCalendarConfiguration/PopupSettingsConfig';
import { format, startOfDay } from 'date-fns';
import { styles } from './YearlyCalendar.styles';
const LEGEND_FALLBACK_COLORS = {
    project: '#008272',
    'non-project': '#0b400b',
    'public holiday': '#5C2D91',
    ross: '#A42E00',
    outlook: '#4098FC',
    pending: '#ffffff',
};
const normalizeLegendLabel = (event) => {
    var _a, _b;
    const category = (event.eventTypeName || '').toLowerCase();
    if (event.isPublicHoliday || category === 'publicholiday' || category === 'msvacation') {
        return 'Public Holiday';
    }
    if (category === 'project') {
        return 'Project';
    }
    if (category === 'outlook') {
        const detail = `${event.name || ''} ${event.subject || ''}`.toLowerCase();
        return (detail.includes('busy') || detail.includes('oof'))
            ? 'Outlook (Busy/OOF)'
            : 'Outlook (Other)';
    }
    if (category === 'nonproject') {
        const assignmentType = ((_a = event.customProperties) === null || _a === void 0 ? void 0 : _a.AssignmentType) || ((_b = event.customProperties) === null || _b === void 0 ? void 0 : _b.eventTypeName);
        return assignmentType || event.name || 'Non-Project';
    }
    return event.eventTypeName || event.name || 'Event';
};
const getLegendColor = (label, event) => {
    const normalized = label.toLowerCase();
    return event.bgcolor
        || LEGEND_FALLBACK_COLORS[normalized]
        || (normalized.includes('outlook') ? LEGEND_FALLBACK_COLORS.outlook : '#605e5c');
};
/**
 * YearlyCalendar - Main calendar shared component
 */
export const YearlyCalendar = memo(({ selectedDate, events, dayLabels, config: configProp, popupSettings: popupSettingsProp, viewMode, activeDate: activeDateProp, onDateChange, onModeChange, onDaySelect, onEventClick, onPopupLeftAction, onAddOrEditEventClick, onDeleteEventClick, onBackClick, backLabel, calendarName = 'Calendar', sideContent, customClass, customAriaLabel, customIconClass, }) => {
    // Merge config with defaults
    const config = useMemo(() => (Object.assign(Object.assign({}, DEFAULT_CALENDAR_CONFIG), configProp)), [configProp]);
    const popupSettings = useMemo(() => (Object.assign(Object.assign({}, DEFAULT_POPUP_SETTINGS), popupSettingsProp)), [popupSettingsProp]);
    const legendItems = useMemo(() => {
        const merged = [
            { label: 'Pending', color: '#ffffff', textColor: '#323130' },
            ...DEFAULT_LEGEND_ITEMS,
        ];
        const seen = new Set(merged.map(item => item.label.toLowerCase()));
        events.forEach((event) => {
            const label = normalizeLegendLabel(event).trim();
            if (!label) {
                return;
            }
            const key = label.toLowerCase();
            if (seen.has(key)) {
                return;
            }
            merged.push({
                label,
                color: getLegendColor(label, event),
                textColor: '#fff',
            });
            seen.add(key);
        });
        return merged;
    }, [events]);
    // Navigation hook
    const navigation = useCalendarNavigation({
        initialDate: selectedDate,
        initialViewMode: config.datepickerMode || 'day',
        viewMode,
        activeDate: activeDateProp,
        minDate: config.minDate,
        maxDate: config.maxDate,
        onDateChange,
        onViewModeChange: (mode) => {
            onModeChange === null || onModeChange === void 0 ? void 0 : onModeChange({ mode, activeDate: navigation.activeDate });
        },
    });
    // Grid hook
    const grid = useCalendarGrid({
        activeDate: navigation.activeDate,
        selectedDate,
        config,
        viewMode: navigation.viewMode,
        customClass,
        customAriaLabel,
        customIconClass,
    });
    // Get visible days from calendar rows (or all 12 months for year view)
    const visibleDays = useMemo(() => {
        const days = [];
        if (navigation.viewMode === 'richcc-year') {
            // Year view: collect days from all 12 months
            grid.yearMonths.forEach(m => m.rows.forEach(r => r.days.forEach(d => {
                if (!d.secondary) {
                    days.push(d.date);
                }
            })));
        }
        else {
            grid.calendarRows.forEach(r => r.days.forEach(d => days.push(d.date)));
        }
        return days;
    }, [navigation.viewMode, grid.calendarRows, grid.yearMonths]);
    // Event processing hook
    const eventProcessing = useEventProcessing({
        events,
        visibleDays,
        startingDay: config.startingDay,
        expandedMode: config.expandedMode,
    });
    // Popup hook
    const popup = usePopupState({
        closeOnEscape: popupSettings.closeOnEscape,
        closeOnOutsideClick: popupSettings.closeOnOutsideClick,
        trapFocus: popupSettings.trapFocus,
    });
    // Callbacks hook
    const callbacks = useCalendarCallbacks({
        onDateChange,
        onModeChange,
        onDaySelect,
        onEventClick,
        onPopupLeftAction,
        onAddOrEditEventClick,
        onDeleteEventClick,
        customClass,
        customAriaLabel,
        customIconClass,
    });
    // Keyboard navigation hook
    const { handleKeyDown } = useKeyboardNavigation({
        enabled: true,
        viewMode: navigation.viewMode,
        onNavigate: navigation.handleKeyNavigation,
        onSelect: () => {
            if (navigation.focusedDate) {
                const day = grid.findDay(`${navigation.focusedDate.getFullYear()}_${navigation.focusedDate.getMonth()}_${navigation.focusedDate.getDate()}`);
                if (day) {
                    const events = eventProcessing.getEvents(day.date);
                    callbacks.handleDayClick(day, events, { stopPropagation: () => { } });
                }
            }
        },
        onEscape: popup.closePopup,
        onToday: navigation.goToToday,
        containerRef: navigation.calendarRef,
    });
    // The grid always keeps exactly one focusable cell (roving tabindex). When the
    // user has not navigated yet, fall back to the selected/active date so keyboard
    // users have a defined entry point instead of every cell being tabbable.
    //
    // Two things are needed for the grid to always expose exactly one tab stop:
    //
    // 1. Normalize to local midnight. Grid cells are generated at midnight, while
    //    `selectedDate` is initialised via `new Date()` and carries a time component.
    // 2. Clamp to a date the current grid actually renders. A focus date left over
    //    from another view mode (or a selected date in a different month) would
    //    otherwise leave NO tabbable cell and make the grid unreachable by keyboard
    //    (WCAG 2.1.1) - the defect this roving tabindex exists to avoid.
    //
    // Normalization must happen before the clamp so the lookup compares like for like.
    const rovingFocusDate = useMemo(() => {
        var _a, _b;
        const base = (_b = (_a = navigation.focusedDate) !== null && _a !== void 0 ? _a : selectedDate) !== null && _b !== void 0 ? _b : navigation.activeDate;
        if (!base) {
            return base;
        }
        const candidate = startOfDay(base);
        if (navigation.viewMode !== 'day') {
            return candidate;
        }
        return grid.getDayPosition(candidate) ? candidate : startOfDay(navigation.activeDate);
    }, [navigation.focusedDate, navigation.viewMode, navigation.activeDate, selectedDate, grid]);
    // Move DOM focus to the roved cell after arrow-key navigation. Guarded so it
    // never steals focus on initial mount - it only acts once focus is already
    // inside the calendar.
    useEffect(() => {
        const container = navigation.calendarRef.current;
        if (!container || !navigation.focusedDate)
            return;
        if (!container.contains(document.activeElement))
            return;
        const key = `${navigation.focusedDate.getFullYear()}_${navigation.focusedDate.getMonth()}_${navigation.focusedDate.getDate()}`;
        const cell = container.querySelector(`[data-date="${key}"]`);
        cell === null || cell === void 0 ? void 0 : cell.focus();
    }, [navigation.focusedDate, navigation.viewMode, navigation.calendarRef]);
    // Generate title based on view mode
    const title = useMemo(() => {
        switch (navigation.viewMode) {
            case 'day':
                return format(navigation.activeDate, config.formatDayTitle || 'MMMM yyyy');
            case 'month':
                return format(navigation.activeDate, config.formatMonthTitle || 'yyyy');
            case 'year':
                return `${grid.yearRange.start} - ${grid.yearRange.end}`;
            case 'richcc-year': {
                const startMonth = format(navigation.activeDate, 'MMM yyyy').toUpperCase();
                const endDate = new Date(navigation.activeDate.getFullYear(), navigation.activeDate.getMonth() + 11, 1);
                const endMonth = format(endDate, 'MMM yyyy').toUpperCase();
                return `${startMonth} - ${endMonth}`;
            }
            default:
                return '';
        }
    }, [navigation.viewMode, navigation.activeDate, config, grid.yearRange]);
    // Handle day click
    const handleDayClick = useCallback((day, events, e) => {
        callbacks.handleDayClick(day, events, e);
        // Open popup if not hidden. Respect showWhenEventsEmpty: skip empty days
        // when the setting is explicitly disabled.
        if (!popupSettings.hidden && !(events.length === 0 && popupSettings.showWhenEventsEmpty === false)) {
            const position = grid.getDayPosition(day.date);
            popup.openPopup(day, events, position === null || position === void 0 ? void 0 : position.rowIndex, position === null || position === void 0 ? void 0 : position.colIndex);
        }
    }, [callbacks, popupSettings.hidden, popupSettings.showWhenEventsEmpty, grid, popup]);
    // Handle event click
    const handleEventClick = useCallback((event, day, e) => {
        callbacks.handleEventClick(event, day, e);
    }, [callbacks]);
    // Handle month select (from month picker)
    const handleMonthSelect = useCallback((month, year) => {
        navigation.selectMonth(month, year);
    }, [navigation]);
    // Handle year select (from year picker)
    const handleYearSelect = useCallback((year) => {
        navigation.selectYear(year);
    }, [navigation]);
    // Handle title click (mode toggle)
    const handleTitleClick = useCallback(() => {
        if (!config.preventModeToggle) {
            navigation.toggleView();
        }
    }, [config.preventModeToggle, navigation]);
    // Render current view
    const renderView = useCallback(() => {
        switch (navigation.viewMode) {
            case 'day':
                return (React.createElement(DayPicker, { rows: grid.calendarRows, dayHeaders: grid.dayHeaders, eventMap: eventProcessing.eventMap, config: config, focusedDate: rovingFocusDate, maxVisibleEvents: config.expandedMode ? Infinity : (config.maxVisibleEvents || 2), onDayClick: handleDayClick, onEventClick: handleEventClick, onMoreClick: (day, events) => popup.openPopup(day, events), onAddEventClick: popupSettings.showLeft === false
                        ? undefined
                        : (day) => callbacks.handlePopupLeftAction(day.date, eventProcessing.eventMap[day.key] || []), onDayFocus: (day) => navigation.setFocusedDate(day.date), getDayCustomClass: (date) => callbacks.getDayCustomClass(date, 'day'), getDayCustomAriaLabel: (date) => callbacks.getDayCustomAriaLabel(date, 'day') }));
            case 'month':
                return (React.createElement(MonthPicker, { activeDate: navigation.activeDate, selectedDate: selectedDate, config: config, onMonthSelect: handleMonthSelect, minDate: config.minDate, maxDate: config.maxDate }));
            case 'year':
                return (React.createElement(YearPicker, { activeDate: navigation.activeDate, selectedDate: selectedDate, config: config, onYearSelect: handleYearSelect, minDate: config.minDate, maxDate: config.maxDate, columns: config.yearColumns, rows: config.yearRows }));
            case 'richcc-year':
                return (React.createElement(YearView, { year: navigation.activeDate.getFullYear(), monthsData: grid.yearMonths, eventMap: eventProcessing.eventMap, config: config, selectedDate: selectedDate, focusedDate: rovingFocusDate, dayHeaders: grid.dayHeaders, onDayClick: (day, events) => handleDayClick(day, events, {}), onEventClick: (event, day) => callbacks.handleEventClick(event, day, {}), onMonthClick: handleMonthSelect, maxVisibleEvents: 1 }));
            default:
                return null;
        }
    }, [
        navigation,
        grid,
        eventProcessing,
        config,
        selectedDate,
        handleDayClick,
        handleEventClick,
        handleMonthSelect,
        handleYearSelect,
        popup,
        popupSettings.showLeft,
        callbacks,
    ]);
    return (React.createElement("div", { ref: navigation.calendarRef, style: styles.container, role: "application", "aria-label": calendarName, onKeyDown: handleKeyDown },
        config.navigation && (React.createElement(CalendarNavigation, { title: title, mode: navigation.viewMode, disabled: config.preventCalNav, hideNav: config.hideCalNav, disableModeToggle: config.preventModeToggle, onPrevious: navigation.viewMode === 'day'
                ? navigation.goToPreviousMonth
                : navigation.goToPreviousYear, onNext: navigation.viewMode === 'day'
                ? navigation.goToNextMonth
                : navigation.goToNextYear, onToday: navigation.goToToday, onTitleClick: handleTitleClick, onBackClick: onBackClick, backLabel: backLabel, legendItems: legendItems })),
        React.createElement("div", { style: styles.viewContainer }, sideContent && navigation.viewMode === 'day' ? (React.createElement("div", { style: styles.monthlyLayout },
            React.createElement("div", { style: styles.monthlyMain }, renderView()),
            React.createElement("div", { style: styles.monthlySide }, sideContent))) : (renderView())),
        !popupSettings.hidden && popup.isOpen && popup.popupState.targetDate && (() => {
            const targetDate = popup.popupState.targetDate;
            const popupDay = {
                date: targetDate,
                label: targetDate.getDate().toString(),
                selected: false,
                disabled: false,
                current: false,
                secondary: false,
                key: `${targetDate.getFullYear()}_${targetDate.getMonth()}_${targetDate.getDate()}`,
                uid: '',
                monthIndex: targetDate.getMonth(),
            };
            return (React.createElement(CalendarPopup, { isOpen: popup.isOpen, date: targetDate, events: popup.popupState.events, position: popup.popupState.position, settings: popupSettings, onClose: popup.closePopup, onEventClick: (event) => {
                    popup.closePopup();
                    callbacks.handleEventClick(event, popupDay, {});
                }, onDeleteEvent: (event) => {
                    popup.closePopup();
                    callbacks.handleDeleteEventClick(event, popupDay);
                }, onLeftAction: () => {
                    popup.closePopup();
                    callbacks.handlePopupLeftAction(targetDate, popup.popupState.events);
                } }));
        })()));
});
YearlyCalendar.displayName = 'YearlyCalendar';
export default YearlyCalendar;
//# sourceMappingURL=YearlyCalendar.js.map